import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import * as communityService from '../../services/community.service';

/**
 * Hive Library (Jenn, 2026-10-24): the pictures and videos members share in
 * My Hive, kept apart from our own Video Library because they're member
 * content and can't be used anywhere else. Files sit on our own server.
 * "Due for review" = over a year old and not kept in the last year; a
 * Team365 To-Do points here every January 15th when anything is due.
 */
const TABS = [
  { key: 'video', label: 'Videos' },
  { key: 'image', label: 'Pictures' },
  { key: 'due', label: 'Due for review' },
];

const fmtSize = (b) => {
  const n = Number(b) || 0;
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};
const fmtLength = (s) => {
  if (!s) return null;
  const t = Math.round(Number(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};
const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

export default function AdminHiveLibrary() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('due') === '1' ? 'due' : (params.get('kind') === 'image' ? 'image' : 'video');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await communityService.listHiveLibrary(tab === 'due' ? { kind: 'all', due: true } : { kind: tab });
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the Hive Library.');
    }
  }, [tab]);

  useEffect(() => { setData(null); load(); }, [load]);

  const pickTab = (key) => setParams(key === 'due' ? { due: '1' } : key === 'image' ? { kind: 'image' } : {});

  const keep = async (id) => {
    setBusyId(id);
    try { await communityService.keepHiveMedia(id); await load(); }
    catch (err) { setError(err.response?.data?.error || 'Could not save that.'); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setBusyId(id);
    try { await communityService.deleteHiveMedia(id); setConfirmId(null); await load(); }
    catch (err) { setError(err.response?.data?.error || 'Could not delete that.'); }
    finally { setBusyId(null); }
  };

  const counts = data?.counts;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <AdminNav />

        <Link to="/admin/bunny-library" className="text-xs text-gray-400 hover:text-reset-brown">← Video Library</Link>
        <div className="mt-2 mb-5">
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Hive Library</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Pictures and videos members shared in My Hive. This is member content: it stays in the Hive and isn't used anywhere else.
          </p>
          {counts && (
            <p className="text-xs text-gray-400 mt-1">
              {counts.videos} video{counts.videos === 1 ? '' : 's'} · {counts.pictures} picture{counts.pictures === 1 ? '' : 's'} · {fmtSize(counts.bytes)} on our server
            </p>
          )}
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-xs font-medium w-fit mb-4">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => pickTab(t.key)}
              className={`px-3 py-1.5 rounded-md transition-colors ${tab === t.key ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500 hover:text-reset-brown'}`}
            >
              {t.label}{t.key === 'due' && counts ? ` (${counts.due})` : ''}
            </button>
          ))}
        </div>

        {tab === 'due' && (
          <p className="text-xs text-gray-500 mb-4">
            Over a year old and not kept in the last year. <strong>Keep</strong> moves one off this list for another year. <strong>Delete</strong> removes the picture or video for good; the member's words stay.
          </p>
        )}

        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

        {!data ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : data.items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">
            {tab === 'due' ? 'Nothing is due for review.' : tab === 'image' ? 'No pictures yet.' : 'No videos yet.'}
          </div>
        ) : (
          <div className="space-y-3">
            {data.items.map(m => (
              <div key={m.id} className="bg-white rounded-2xl shadow-sm p-3 flex gap-3 items-start">
                <a href={m.url} target="_blank" rel="noreferrer" className="w-40 h-28 shrink-0 rounded-lg overflow-hidden bg-gray-100 block">
                  {m.kind === 'video'
                    ? <video src={`${m.url}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    : <img src={m.url} alt={m.alt_text || ''} className="w-full h-full object-cover" />}
                </a>
                <div className="flex-1 min-w-0 text-sm">
                  <p className="font-semibold text-reset-brown">{m.member_name}</p>
                  <p className="text-xs text-gray-400">
                    {fmtDate(m.created_at)} · {m.comment_id ? 'on a comment' : 'on a post'}
                    {m.kind === 'video' && fmtLength(m.duration_seconds) ? ` · ${fmtLength(m.duration_seconds)}` : ''}
                    {` · ${fmtSize(m.size_bytes)}`}
                    {m.reviewed_at ? ` · kept ${fmtDate(m.reviewed_at)}` : ''}
                  </p>
                  <p className={`text-xs mt-1 ${m.alt_text ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                    {m.alt_text || 'No description'}
                  </p>
                  {m.thread_post_id && (
                    <Link to={`/dashboard/discussion/${m.thread_post_id}`} className="text-xs text-reset-terracotta font-medium hover:underline mt-1 inline-block">
                      Open the post →
                    </Link>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {tab === 'due' && (
                    <button
                      onClick={() => keep(m.id)}
                      disabled={busyId === m.id}
                      className="px-3 py-1.5 bg-reset-sage text-white text-xs font-medium rounded-lg hover:bg-reset-brown disabled:opacity-50"
                    >
                      Keep
                    </button>
                  )}
                  {confirmId === m.id ? (
                    <div className="flex gap-1.5">
                      <button onClick={() => setConfirmId(null)} className="text-xs text-gray-400 hover:text-reset-brown">Cancel</button>
                      <button
                        onClick={() => remove(m.id)}
                        disabled={busyId === m.id}
                        className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown disabled:opacity-50"
                      >
                        {busyId === m.id ? 'Deleting…' : 'Yes, delete'}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmId(m.id)} className="text-xs text-gray-400 hover:text-reset-terracotta">Delete</button>
                  )}
                </div>
              </div>
            ))}
            {data.total > data.items.length && (
              <p className="text-xs text-gray-400 text-center">Showing the oldest {data.items.length} of {data.total}.</p>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
